import type {
  MoveAction,
  OblivionMineMine,
  OblivionMinePlayerId,
  OblivionMinePosition,
  OblivionMineState,
  OblivionMineTreasure,
  OblivionMineViewerState,
} from "../types";
import {
  OBLIVION_MINE_BOARD_SIZE,
  OBLIVION_MINE_PLAYERS,
  OBLIVION_MINE_STARTS,
  OBLIVION_MINE_TREASURE_CELLS,
} from "../types";

const ORTHOGONAL_DIRECTIONS = [
  { row: -1, col: 0 },
  { row: 1, col: 0 },
  { row: 0, col: -1 },
  { row: 0, col: 1 },
] as const;

export function positionKey(position: OblivionMinePosition): string {
  return `${position.row},${position.col}`;
}

export function positionsEqual(
  a: OblivionMinePosition,
  b: OblivionMinePosition,
): boolean {
  return a.row === b.row && a.col === b.col;
}

export function isInBounds(row: number, col: number): boolean {
  return (
    Number.isInteger(row) &&
    Number.isInteger(col) &&
    row >= 0 &&
    row < OBLIVION_MINE_BOARD_SIZE &&
    col >= 0 &&
    col < OBLIVION_MINE_BOARD_SIZE
  );
}

export function isPlayerId(value: unknown): value is OblivionMinePlayerId {
  return (
    typeof value === "string" &&
    OBLIVION_MINE_PLAYERS.includes(value as OblivionMinePlayerId)
  );
}

export function getOpponentPlayerId(
  playerId: OblivionMinePlayerId,
): OblivionMinePlayerId {
  return playerId === "p1" ? "p2" : "p1";
}

export function isStartCell(row: number, col: number): boolean {
  return OBLIVION_MINE_PLAYERS.some((playerId) => {
    const start = OBLIVION_MINE_STARTS[playerId];
    return start.row === row && start.col === col;
  });
}

export function isTreasureCell(row: number, col: number): boolean {
  return OBLIVION_MINE_TREASURE_CELLS.some(
    (cell) => cell.row === row && cell.col === col,
  );
}

/** A legal cell to lay a mine on: in bounds, not a start or treasure cell. */
export function isMineableCell(row: number, col: number): boolean {
  return (
    isInBounds(row, col) && !isStartCell(row, col) && !isTreasureCell(row, col)
  );
}

export function isOrthogonalStep(
  from: OblivionMinePosition,
  to: OblivionMinePosition,
): boolean {
  return Math.abs(from.row - to.row) + Math.abs(from.col - to.col) === 1;
}

export function manhattan(
  a: OblivionMinePosition,
  b: OblivionMinePosition,
): number {
  return Math.abs(a.row - b.row) + Math.abs(a.col - b.col);
}

/**
 * Index of an ARMED mine owned by the mover's opponent at (row,col), else -1.
 * Own mines and already-triggered mines never count — only an opponent's live
 * mine causes a hit.
 */
export function findArmedOpponentMineIndex(
  mines: readonly OblivionMineMine[],
  row: number,
  col: number,
  mover: OblivionMinePlayerId,
): number {
  return mines.findIndex(
    (mine) =>
      mine.owner !== mover &&
      !mine.triggered &&
      mine.row === row &&
      mine.col === col,
  );
}

/** Index of an uncollected treasure at (row,col), else -1. */
export function uncollectedTreasureIndexAt(
  treasures: readonly OblivionMineTreasure[],
  row: number,
  col: number,
): number {
  return treasures.findIndex(
    (treasure) =>
      treasure.collectedBy === null &&
      treasure.row === row &&
      treasure.col === col,
  );
}

/** Orthogonal in-bounds neighbours of the player's piece (all legal moves). */
export function enumerateLegalMoves(
  state: OblivionMineState,
  playerId: OblivionMinePlayerId = state.currentPlayerId,
): readonly MoveAction[] {
  if (state.phase !== "move") return [];
  const from = state.positions[playerId];
  const moves: MoveAction[] = [];
  for (const direction of ORTHOGONAL_DIRECTIONS) {
    const row = from.row + direction.row;
    const col = from.col + direction.col;
    if (!isInBounds(row, col)) continue;
    moves.push({ type: "Move", playerId, to: { row, col } });
  }
  return moves;
}

/** The winner if a player has collected enough treasures, else null. */
export function checkWinner(
  scores: Readonly<Record<OblivionMinePlayerId, number>>,
  treasuresToWin: number,
): OblivionMinePlayerId | null {
  for (const playerId of OBLIVION_MINE_PLAYERS) {
    if (scores[playerId] >= treasuresToWin) return playerId;
  }
  return null;
}

/**
 * Asymmetric viewer projection. The returned `mines` array contains ONLY the
 * mines this viewer may see — own mines (when the viewer is a player) plus any
 * triggered mine. An opponent's armed mine is omitted entirely, so its position
 * and existence never reach the client. A null viewer (observer/replay) sees
 * only triggered mines. Hidden information stays server-authoritative.
 */
export function filterStateForViewer(
  state: OblivionMineState,
  viewerPlayerId: string | null,
): OblivionMineViewerState {
  const visibleMines = state.mines.filter(
    (mine) =>
      mine.triggered ||
      (viewerPlayerId !== null && mine.owner === viewerPlayerId),
  );
  return { ...state, mines: visibleMines };
}

export function buildViewerStates(
  state: OblivionMineState,
  observerUserIds: readonly string[] = [],
): Readonly<Record<string, OblivionMineViewerState>> {
  const playerEntries = state.players.map(
    (player) =>
      [player.playerId, filterStateForViewer(state, player.playerId)] as const,
  );
  const observerEntries = [...new Set(observerUserIds)].map(
    (observerUserId) =>
      [observerUserId, filterStateForViewer(state, null)] as const,
  );
  return Object.fromEntries([...playerEntries, ...observerEntries]);
}
